import { Box } from "@mui/material";

import { withFieldGroup } from "components/Forms/AppForm";

import type { FileUploadMetadata } from "./FileUploadSection.types";

const fileUploadGroupDefaults = {
  fileType: "",
  documentVisibility: "",
  fileLabel: "",
  files: [] as FileUploadMetadata[],
};

const FileUploadSection = withFieldGroup({
  defaultValues: fileUploadGroupDefaults,
  props: {
    uploadTypeOptions: [] as string[],
    visibilityOptions: undefined as string[] | undefined,
  },
  render: function Render({ group, uploadTypeOptions, visibilityOptions }) {
    const hasVisibility = Boolean(visibilityOptions);
    const columns = hasVisibility ? 4 : 3;

    return (
      <group.FormSection
        title="Attach Files"
        columns={columns}
        fieldContainerProps={{ sx: { alignItems: "baseline" } }}
      >
        {/* File Type */}
        <group.AppField
          name="fileType"
          listeners={{
            onChange: ({ value }) => {
              group.setFieldValue("fileLabel", value ?? "");
            },
          }}
        >
          {(field) => (
            <field.SelectField
              options={uploadTypeOptions}
              label="Type"
              sx={{ flex: 1 }}
            />
          )}
        </group.AppField>

        {/* Document Visibility (optional) */}
        {hasVisibility && (
          <group.AppField name="documentVisibility">
            {(field) => (
              <field.SelectField
                options={visibilityOptions!}
                label="Document Visibility"
                sx={{ flex: 1 }}
              />
            )}
          </group.AppField>
        )}

        {/* File Label */}
        <group.AppField name="fileLabel">
          {(field) => (
            <field.TextInput
              label="File Label"
              sx={{
                "&": {
                  flex: "2 2 50%",
                  maxWidth: "calc(50% - 8px)",
                  height: "100%",
                  alignSelf: "baseline",
                },
              }}
            />
          )}
        </group.AppField>

        {/* File Upload Button */}
        <group.Subscribe
          selector={(state) => {
            const { fileType, fileLabel, documentVisibility } = state.values;
            const baseDisabled = !fileType || !fileLabel;
            return hasVisibility
              ? baseDisabled || !documentVisibility
              : baseDisabled;
          }}
        >
          {(disabled) => (
            <group.AppField
              name="files"
              listeners={{
                onChange: () => {
                  group.resetField("fileType");
                  group.resetField("fileLabel");
                  if (hasVisibility) {
                    group.resetField("documentVisibility");
                  }
                },
              }}
            >
              {(field) => (
                <field.FileUpload
                  disabled={disabled}
                  label="Choose File"
                  annotateFile={(file: File) => {
                    const metadata: FileUploadMetadata = {
                      fileType: group.getFieldValue("fileType"),
                      fileLabel: group.getFieldValue("fileLabel"),
                      fileName: file.name,
                      active: true,
                    };
                    if (hasVisibility) {
                      metadata.documentVisibility =
                        group.getFieldValue("documentVisibility");
                    }
                    return metadata;
                  }}
                />
              )}
            </group.AppField>
          )}
        </group.Subscribe>

        {/* File Display List */}
        <Box
          flex="1 1 100%"
          display="flex"
          gap={2}
          flexWrap="wrap"
          sx={{ marginLeft: 1, paddingLeft: 1 }}
        >
          <group.AppField name="files">
            {(field) =>
              field.state.value
                .filter((file: FileUploadMetadata) => file.active)
                .map((file: FileUploadMetadata, index: number) => (
                  <field.FileCard
                    file={(file.file || file) as unknown as File}
                    title={file.fileLabel || file.fileType}
                    subheader={file.documentVisibility}
                    fileName={file.fileName}
                    index={index}
                    key={file.file_id || file.fileName}
                  />
                ))
            }
          </group.AppField>
        </Box>
      </group.FormSection>
    );
  },
});

export default FileUploadSection;
